import AddIcon from "@mui/icons-material/AddRounded";
import { Button, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useMemo } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import RoundedTable from "@entrilia/shared/components/RoundedTable";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { groupByToMap } from "@entrilia/shared/utilities/arrayHelper";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import adminApi from "../../../../../../api/adminApi";
import useClientContext from "../../../../../../context/useClientContext";
import TransactionEditContext from "./TransactionEditContext";
import type { TransactionLineRelatedEntities } from "./transactionEditState";
import { addTransactionLineAction, fillTransactionLinesAction } from "./transactionEditState";
import TransactionLineRow from "./TransactionLineRow";

const loadAllRelatedEntities = async (): Promise<ApiResponse<TransactionLineRelatedEntities>> => {
  const [transactionCodesResp, positionTypesResp, accountsResp, allocationRulesResp] = await Promise.all([
    adminApi.searchObjects("TransactionCode", {}),
    adminApi.searchObjects("PositionType", {}),
    adminApi.searchObjects("GlAccount", {}),
    adminApi.searchObjects("AllocationRule", {}),
  ]);

  if (!transactionCodesResp.success) {
    return { success: false, error: transactionCodesResp.error };
  }
  if (!positionTypesResp.success) {
    return { success: false, error: positionTypesResp.error };
  }
  if (!accountsResp.success) {
    return { success: false, error: accountsResp.error };
  }
  if (!allocationRulesResp.success) {
    return { success: false, error: allocationRulesResp.error };
  }

  return {
    success: true,
    data: {
      transactionCodes: transactionCodesResp.data.items,
      positionTypes: positionTypesResp.data.items,
      accounts: accountsResp.data.items,
      allocationRules: allocationRulesResp.data.items,
    },
  };
};

const TransactionLinesTable = () => {
  const { activityChangeSet, editState, setEditState } = useDefinedContext(TransactionEditContext);

  const { dictionaries } = useClientContext();

  const [allRelatedEntities, fetchError] = useFetch(loadAllRelatedEntities, {
    onSetData: (data) => setEditState(fillTransactionLinesAction(activityChangeSet, data)),
  });

  const groupedAllocationRules = useMemo(() => {
    const allRules = allRelatedEntities?.allocationRules ?? [];
    return groupByToMap(allRules, (rule) => stringifyValue(rule.fieldValues["Level"]));
  }, [allRelatedEntities?.allocationRules]);

  const currencyCodes = useMemo(() => dictionaries.currencies.map(({ code }) => code), [dictionaries]);

  if (fetchError) {
    logError(fetchError, "[TransactionLinesTable]");
    return <DataLoadingFailed title="Failed to fetch required entities" />;
  }

  if (allRelatedEntities === undefined) {
    return <InlineLoader />;
  }

  if (editState.lines.length === 0) {
    return (
      <Typography color="textSecondary" sx={{ textAlign: "center", mt: 1, mb: 3 }}>
        No lines
      </Typography>
    );
  }

  return (
    <>
      <RoundedTable noPaddingInBodyCells>
        <TableHead>
          <TableRow>
            <TableCell>Transaction Code</TableCell>
            <TableCell>Position Type</TableCell>
            <TableCell>Position No</TableCell>
            <TableCell>G/L Account</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Shares</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Currency Code</TableCell>
            <TableCell>{editState.isMultiEntity ? "Entity Allocation Rule" : "Investor Allocation Rule"}</TableCell>
            <TableCell width={32} />
          </TableRow>
        </TableHead>
        <TableBody>
          {editState.lines.map((line) => (
            <TransactionLineRow
              key={line.changeSetId}
              line={line}
              allTransactionCodes={allRelatedEntities.transactionCodes}
              allPositionTypes={allRelatedEntities.positionTypes}
              allAccounts={allRelatedEntities.accounts}
              allEntityAllocationRules={groupedAllocationRules.get("Entity") ?? []}
              allInvestorAllocationRules={groupedAllocationRules.get("Investor") ?? []}
              currencyCodes={currencyCodes}
            />
          ))}
        </TableBody>
      </RoundedTable>

      <Button
        variant="text"
        startIcon={<AddIcon />}
        onClick={() => setEditState(addTransactionLineAction())}
        sx={{ mt: 1 }}
      >
        Add New
      </Button>
    </>
  );
};

export default TransactionLinesTable;
